import React, { useEffect, useCallback, useRef } from 'react';
import {
  YEAR_END_MODAL_OPEN_EVENT,
  YEAR_END_MODAL_CLOSE_EVENT,
  getYearEndModalContext,
  closeYearEndModal
} from '../utils/yearEndModalBus';
import { supabase } from '../lib/supabase_client';

export const YearEndChangeModal: React.FC = () => {
  // Main modal refs
  const modalRootRef = useRef<HTMLDivElement>(null);
  const modalBackdropRef = useRef<HTMLDivElement>(null);
  const modalPanelRef = useRef<HTMLDivElement>(null);
  
  const firstYearRef = useRef<HTMLSelectElement>(null);
  const currentStartRef = useRef<HTMLInputElement>(null);
  const currentEndRef = useRef<HTMLInputElement>(null);
  const previousStartRef = useRef<HTMLInputElement>(null);
  const previousEndRef = useRef<HTMLInputElement>(null);
  const previousFieldsRef = useRef<HTMLDivElement>(null);
  
  const contextRef = useRef<any>(null);
  
  // Mounted guard
  const mountedRef = useRef(true);
  
  // Refs to track if secondary modals exist (singleton pattern)
  const warningModalRef = useRef<HTMLElement | null>(null);
  const processingModalRef = useRef<HTMLElement | null>(null);
  
  // Refs for event listeners cleanup
  const eventListenersRef = useRef<Array<{element: HTMLElement, event: string, handler: any}>>([]);

  // Helper to add event listener and track for cleanup
  const addTrackedListener = useCallback((element: HTMLElement | null, event: string, handler: any) => {
    if (!element || !mountedRef.current) return;
    
    element.addEventListener(event, handler);
    eventListenersRef.current.push({ element, event, handler });
  }, []);

  // Helper to clean up all tracked listeners
  const cleanupListeners = useCallback(() => {
    eventListenersRef.current.forEach(({ element, event, handler }) => {
      element?.removeEventListener(event, handler);
    });
    eventListenersRef.current = [];
  }, []);

  const handleFirstYearChange = useCallback(() => {
    if (!mountedRef.current) return;

    const firstYear = firstYearRef.current?.value;
    const previousFields = previousFieldsRef.current;
    const previousStart = previousStartRef.current;
    const previousEnd = previousEndRef.current;

    if (!previousFields || !previousStart || !previousEnd) return;

    if (firstYear === 'Y') {
      previousFields.style.display = 'none';
      previousStart.value = '';
      previousEnd.value = '';
    } else if (firstYear === 'N') {
      previousFields.style.display = 'flex';
      previousFields.style.flexDirection = 'column';
      
      const currentStart = currentStartRef.current?.value;
      if (currentStart) {
        handleCurrentStartChange();
      }
    } else {
      previousFields.style.display = 'flex';
      previousFields.style.flexDirection = 'column';
    }
  }, []);

  const handleCurrentStartChange = useCallback(() => {
    if (!mountedRef.current) return;

    const currentStart = currentStartRef.current?.value;
    const firstYear = firstYearRef.current?.value;
    const previousEnd = previousEndRef.current;

    if (!previousEnd) return;

    if (firstYear === 'N' && currentStart) {
      const currentStartDate = new Date(currentStart);
      const previousEndDate = new Date(currentStartDate);
      previousEndDate.setDate(previousEndDate.getDate() - 1);
      
      const formattedDate = previousEndDate.toISOString().split('T')[0];
      previousEnd.value = formattedDate;
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    const firstYear = firstYearRef.current?.value;
    const currentStart = currentStartRef.current?.value;
    const currentEnd = currentEndRef.current?.value;
    const previousStart = previousStartRef.current?.value;
    const previousEnd = previousEndRef.current?.value;

    if (!firstYear) {
      alert('Please select whether this is the first year of operation.');
      return false;
    }

    if (!currentStart || !currentEnd) {
      alert('Please fill in current financial year start and end dates.');
      return false;
    }

    if (firstYear === 'N' && !previousStart) {
      alert('Please fill in previous financial year start date.');
      return false;
    }

    if (new Date(currentStart) >= new Date(currentEnd)) {
      alert('Current year start date must be before end date.');
      return false;
    }

    if (firstYear === 'N' && previousStart && previousEnd) {
      if (new Date(previousStart) >= new Date(previousEnd)) {
        alert('Previous year start date must be before end date.');
        return false;
      }
    }

    return true;
  }, []);

  const showWarningModal = useCallback(() => {
    if (!mountedRef.current) return;

    // Check if modal already exists
    let warningModal = document.getElementById('yearEndWarningModal');
    
    if (!warningModal) {
      const warningHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10001; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 600px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: var(--theme-400); margin: 0;">⚠️ Critical Warning</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Data Will Be Overwritten</p>
              </div>
              <button type="button" id="yearEndWarningClose" style="display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; border: none; background: transparent; color: rgb(100, 116, 139); cursor: pointer; transition: all 0.15s; font-size: 24px; line-height: 1;">×</button>
            </div>
            <div style="padding: 24px 20px; max-height: 60vh; overflow-y: auto;">
              <div style="background: #fef2f2; padding: 16px; border-radius: 8px; margin-bottom: 16px; border-left: 4px solid #dc2626;">
                <strong style="display: block; margin-bottom: 8px; color: #991b1b;">Changing the year-end will:</strong>
                <ul style="margin: 0; padding-left: 20px; color: #7f1d1d; line-height: 1.6;">
                  <li>Reset all manually entered values</li>
                  <li>Re-process the document with new year-end dates</li>
                </ul>
                <strong style="display: block; margin-top: 12px; color: #7f1d1d;">This action cannot be undone!</strong>
              </div>
              <div style="background: #fffbeb; padding: 16px; border-radius: 8px; margin-bottom: 16px;">
                <strong style="display: block; margin-bottom: 8px; color: #78350f;">Process details:</strong>
                <ul style="margin: 0; padding-left: 20px; color: #78350f; line-height: 1.6;">
                  <li>Year-end conversion will take around 2-5 minutes</li>
                  <li>You will be redirected to filings page after completion</li>
                </ul>
              </div>
              <div style="color: #dc2626; font-size: 14px; font-weight: 600; text-align: center;">
                Only proceed if you understand that all current changes will be lost!
              </div>
            </div>
            <div class="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
              <button type="button" id="yearEndWarningCancel" class="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400">
                Cancel - Keep My Changes
              </button>
              <button type="button" id="yearEndWarningProceed" class="inline-flex items-center justify-center rounded bg-red-600 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-sky-400">
                I Understand - Proceed
              </button>
            </div>
          </div>
        </div>
      `;

      const warningDiv = document.createElement('div');
      warningDiv.id = 'yearEndWarningModal';
      warningDiv.innerHTML = warningHtml;
      document.body.appendChild(warningDiv);
      
      warningModal = warningDiv;
      warningModalRef.current = warningDiv;
    }

    // Show modal
    (warningModal as HTMLElement).style.display = 'flex';

    // Attach event listeners
    requestAnimationFrame(() => {
      if (!mountedRef.current) return;

      const hideWarning = () => {
        if (warningModalRef.current) {
          warningModalRef.current.style.display = 'none';
        }
      };

      const closeBtn = document.getElementById('yearEndWarningClose');
      const cancelBtn = document.getElementById('yearEndWarningCancel');
      const proceedBtn = document.getElementById('yearEndWarningProceed');

      if (closeBtn) {
        const hoverIn = () => (closeBtn as HTMLElement).style.background = 'rgb(241, 245, 249)';
        const hoverOut = () => (closeBtn as HTMLElement).style.background = 'transparent';
        
        addTrackedListener(closeBtn, 'click', hideWarning);
        addTrackedListener(closeBtn, 'mouseenter', hoverIn);
        addTrackedListener(closeBtn, 'mouseleave', hoverOut);
      }
      
      if (cancelBtn) {
        addTrackedListener(cancelBtn, 'click', hideWarning);
      }
      
      if (proceedBtn) {
        addTrackedListener(proceedBtn, 'click', () => {
          hideWarning();
          proceedWithYearEndChange();
        });
      }
    });
  }, [mountedRef, addTrackedListener]);

  const showProcessingModal = useCallback(() => {
    if (!mountedRef.current) return;

    // Check if modal already exists
    let processingModal = document.getElementById('yearEndProcessingModal');
    
    if (!processingModal) {
      const processingHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10002; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 500px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: var(--theme-400); margin: 0;">🔄 Processing Year-End Change</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Please wait</p>
              </div>
            </div>
            <div style="padding: 32px 24px; text-align: center;">
              <div style="font-size: 16px; margin-bottom: 20px; color: #374151; font-weight: 500;" id="yearEndProcessingStep">
                Updating financial year dates...
              </div>
              <div style="background: #f3f4f6; border-radius: 10px; height: 8px; margin: 24px 0; overflow: hidden;">
                <div id="yearEndProcessingProgress" style="background: #3b82f6; height: 100%; width: 25%; transition: width 0.5s ease;"></div>
              </div>
              <div style="color: #6b7280; font-size: 14px;">
                This process takes about 3-5 minutes
              </div>
            </div>
          </div>
        </div>
      `;

      const processingDiv = document.createElement('div');
      processingDiv.id = 'yearEndProcessingModal';
      processingDiv.innerHTML = processingHtml;
      document.body.appendChild(processingDiv);
      
      processingModal = processingDiv;
      processingModalRef.current = processingDiv;
    }

    // Show modal
    (processingModal as HTMLElement).style.display = 'flex';

    // Simulate progress steps
    setTimeout(() => {
      if (!mountedRef.current) return;
      const stepEl = document.getElementById('yearEndProcessingStep');
      const progressEl = document.getElementById('yearEndProcessingProgress');
      if (stepEl) stepEl.textContent = 'Re-processing document data...';
      if (progressEl) progressEl.style.width = '50%';
    }, 1000);

    setTimeout(() => {
      if (!mountedRef.current) return;
      const stepEl = document.getElementById('yearEndProcessingStep');
      const progressEl = document.getElementById('yearEndProcessingProgress');
      if (stepEl) stepEl.textContent = 'Updating comparative figures...';
      if (progressEl) progressEl.style.width = '75%';
    }, 2000);

    setTimeout(() => {
      if (!mountedRef.current) return;
      const stepEl = document.getElementById('yearEndProcessingStep');
      const progressEl = document.getElementById('yearEndProcessingProgress');
      if (stepEl) stepEl.textContent = 'Finalizing changes...';
      if (progressEl) progressEl.style.width = '100%';
    }, 3000);
  }, [mountedRef]);

  const showSuccessCountdown = useCallback((onRedirect: () => void) => {
    if (!mountedRef.current) return;

    let countdown = 5;

    const updateCountdown = () => {
      if (!mountedRef.current) return;

      const successHtml = `
        <div style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px); display: flex; align-items: center; justify-content: center; z-index: 10002; padding: 16px;">
          <div style="background: white; border-radius: 12px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); max-width: 500px; width: 100%; overflow: hidden;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgb(226, 232, 240); padding: 10px 12px;">
              <div>
                <h3 style="font-size: 16px; font-weight: 600; color: #059669; margin: 0;">✅ Year-End Change Completed</h3>
                <p style="margin: 4px 0 0 0; font-size: 12px; color: rgb(100, 116, 139);">Successfully updated</p>
              </div>
            </div>
            <div style="padding: 32px 24px; text-align: center;">
              <div style="font-size: 16px; margin-bottom: 20px; color: #059669; font-weight: 500;">
                Financial year dates successfully updated!
              </div>
              <div style="background: #d1fae5; padding: 16px; border-radius: 8px; margin: 20px 0; color: #065f46; text-align: left; line-height: 1.6;">
                <strong>Document has been re-processed with new year-end dates.</strong><br>
                All previous amendments have been reset.
              </div>
              <div style="margin: 20px 0; color: #374151; font-size: 15px;">
                Redirecting to filings page in <strong id="yearEndCountdown" style="color: #3b82f6;">${countdown}</strong> seconds...
              </div>
            </div>
            <div class="flex items-center justify-center gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
              <button type="button" id="yearEndGoNow" class="inline-flex items-center justify-center rounded bg-slate-900 px-4 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400">
                Go Now
              </button>
            </div>
          </div>
        </div>
      `;

      const modal = processingModalRef.current;
      if (modal) {
        modal.innerHTML = successHtml;
        
        // Attach event listener
        requestAnimationFrame(() => {
          if (!mountedRef.current) return;
          
          const goNowBtn = document.getElementById('yearEndGoNow');
          if (goNowBtn) {
            addTrackedListener(goNowBtn, 'click', () => {
              if (processingModalRef.current) {
                processingModalRef.current.style.display = 'none';
              }
              onRedirect();
            });
          }
        });
      }

      if (countdown > 0) {
        setTimeout(() => {
          countdown--;
          updateCountdown();
        }, 1000);
      } else {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        onRedirect();
      }
    };

    updateCountdown();
  }, [mountedRef, addTrackedListener]);

  const proceedWithYearEndChange = useCallback(async () => {
    const ctx = contextRef.current;
    if (!ctx || !mountedRef.current) return;

    const firstYear = firstYearRef.current?.value;
    const currentStart = currentStartRef.current?.value;
    const currentEnd = currentEndRef.current?.value;
    const previousStart = previousStartRef.current?.value;
    const previousEnd = previousEndRef.current?.value;

    if (!firstYear || !currentStart || !currentEnd) return;

    let disclosureValue;
    if (firstYear === 'Y') {
      disclosureValue = 'First time preparation of financial statements after incorporation';
    } else if (firstYear === 'N') {
      disclosureValue = 'Subsequent preparation of financial statements';
    }

    close();
    showProcessingModal();

    try {
      const { data, error } = await supabase.functions.invoke('trigger-year-end-change', {
        body: {
          filing_id: ctx.filingId,
          org_id: ctx.orgId,
          first_year: firstYear,
          disclosure_value: disclosureValue,
          current_start: currentStart,
          current_end: currentEnd,
          previous_start: previousStart || null,
          previous_end: previousEnd || null
        }
      });

      if (!mountedRef.current) return;

      if (error) {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        alert(`Error: ${error.message || 'Failed to trigger year-end change'}`);
        return;
      }

      if (data?.status === 'success') {
        showSuccessCountdown(ctx.onSuccess);
      } else {
        if (processingModalRef.current) {
          processingModalRef.current.style.display = 'none';
        }
        alert(`Error: ${data?.error || 'Unknown error occurred'}`);
      }
    } catch (error: any) {
      if (!mountedRef.current) return;
      
      if (processingModalRef.current) {
        processingModalRef.current.style.display = 'none';
      }
      alert(`Network error: ${error.message}`);
    }
  }, [showSuccessCountdown, showProcessingModal, mountedRef]);

  const handleConfirm = useCallback(() => {
    if (!validateForm()) return;
    showWarningModal();
  }, [validateForm, showWarningModal]);

  const open = useCallback(() => {
    if (!mountedRef.current) return;

    const ctx = getYearEndModalContext();
    if (!ctx) return;

    contextRef.current = ctx;

    if (firstYearRef.current) firstYearRef.current.value = ctx.firstYear || '';
    if (currentStartRef.current) currentStartRef.current.value = ctx.currentStart || '';
    if (currentEndRef.current) currentEndRef.current.value = ctx.currentEnd || '';
    if (previousStartRef.current) previousStartRef.current.value = ctx.previousStart || '';
    if (previousEndRef.current) previousEndRef.current.value = ctx.previousEnd || '';

    handleFirstYearChange();

    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'flex';
      document.body.style.overflow = 'hidden';
    }
  }, [handleFirstYearChange, mountedRef]);

  const close = useCallback(() => {
    if (!mountedRef.current) return;

    if (modalRootRef.current) {
      modalRootRef.current.style.display = 'none';
      document.body.style.overflow = '';
    }
  }, [mountedRef]);

  useEffect(() => {
    document.addEventListener(YEAR_END_MODAL_OPEN_EVENT, open);
    document.addEventListener(YEAR_END_MODAL_CLOSE_EVENT, close);

    return () => {
      // Cleanup on unmount
      mountedRef.current = false;
      
      document.removeEventListener(YEAR_END_MODAL_OPEN_EVENT, open);
      document.removeEventListener(YEAR_END_MODAL_CLOSE_EVENT, close);
      
      // Clean up all tracked event listeners
      cleanupListeners();
      
      // Remove secondary modals from DOM
      if (warningModalRef.current) {
        warningModalRef.current.remove();
        warningModalRef.current = null;
      }
      if (processingModalRef.current) {
        processingModalRef.current.remove();
        processingModalRef.current = null;
      }
      
      // Reset body overflow
      document.body.style.overflow = '';
    };
  }, [open, close, cleanupListeners]);

  return (
    <div
      ref={modalRootRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
    >
      <div
        ref={modalBackdropRef}
        onClick={closeYearEndModal}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)'
        }}
      />

      <div
        ref={modalPanelRef}
        style={{
          position: 'relative',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          maxWidth: '600px',
          width: '100%',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2.5">
          <div>
            <h3 className="text-base font-semibold text-[var(--theme-400)]" style={{ margin: 0 }}>
              Modify Year End
            </h3>
            <p className="mt-0.5 text-xs text-slate-500" style={{ margin: '4px 0 0 0' }}>
              Change financial year dates
            </p>
          </div>
          <button
            type="button"
            onClick={closeYearEndModal}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-400"
            aria-label="Close"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                First Year of Operation <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <select
                ref={firstYearRef}
                onChange={handleFirstYearChange}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#111827'
                }}
              >
                <option value="">Please Select</option>
                <option value="Y">Yes - First year</option>
                <option value="N">No - Subsequent year</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Current Financial Year <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Start Date</label>
                  <input
                    ref={currentStartRef}
                    type="date"
                    onChange={handleCurrentStartChange}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>End Date</label>
                  <input
                    ref={currentEndRef}
                    type="date"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
            </div>

            <div ref={previousFieldsRef} style={{ display: 'none', flexDirection: 'column' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                Previous Financial Year
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Start Date</label>
                  <input
                    ref={previousStartRef}
                    type="date"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>End Date</label>
                  <input
                    ref={previousEndRef}
                    type="date"
                    readOnly
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: '#f9fafb',
                      color: '#6b7280'
                    }}
                  />
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', fontStyle: 'italic' }}>
                Previous end date auto-calculated as one day before current start
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2.5">
          <button
            type="button"
            onClick={closeYearEndModal}
            className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="inline-flex items-center justify-center rounded bg-slate-900 px-3 py-1.5 text-[13px] font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-400"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};
